import {
  type CreateHotelInventoryRequest,
  type CreateHotelRequest,
  type CreateHotelRoomRequest,
  type HotelApprovalRequest,
  type HotelDetail,
  HotelDetailSchema,
  type HotelFacets,
  HotelFacetsSchema,
  type HotelInventoryListItem,
  HotelInventoryListItemSchema,
  type HotelInventoryListQuery,
  type HotelInventoryListResponse,
  HotelInventoryListResponseSchema,
  type HotelListQuery,
  type HotelListResponse,
  HotelListResponseSchema,
  type HotelRoomOut,
  HotelRoomOutSchema,
  type UpdateHotelInventoryRequest,
  type UpdateHotelRequest,
  type UpdateHotelRoomRequest,
} from '@utazzo/shared';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

/**
 * Hotel master API. Every response is validated with the shared Zod schema
 * so a server change can't sneak past the UI types.
 */

export const listHotels = async (query: HotelListQuery): Promise<HotelListResponse> => {
  const { data } = await apiClient.get<unknown>('/hotels', { params: query });
  return HotelListResponseSchema.parse(data);
};

/**
 * Facets for the Country / City list filters - only the countries and cities
 * actually used by hotels (not the whole geography master). Grows as hotels in
 * new places are added.
 */
export const getHotelFacets = async (): Promise<HotelFacets> => {
  const { data } = await apiClient.get<unknown>('/hotels/facets');
  return HotelFacetsSchema.parse(data);
};

export const getHotel = async (id: string): Promise<HotelDetail> => {
  const { data } = await apiClient.get<unknown>(`/hotels/${id}`);
  return HotelDetailSchema.parse(data);
};

export const createHotel = async (payload: CreateHotelRequest): Promise<HotelDetail> => {
  const { data } = await apiClient.post<unknown>('/hotels', payload);
  return HotelDetailSchema.parse(data);
};

export const updateHotel = async (
  id: string,
  payload: UpdateHotelRequest,
): Promise<HotelDetail> => {
  const { data } = await apiClient.patch<unknown>(`/hotels/${id}`, payload);
  return HotelDetailSchema.parse(data);
};

export const approveHotel = async (
  id: string,
  payload: HotelApprovalRequest,
): Promise<HotelDetail> => {
  const { data } = await apiClient.post<unknown>(`/hotels/${id}/approval`, payload);
  return HotelDetailSchema.parse(data);
};

export const deleteHotel = async (id: string): Promise<void> => {
  await apiClient.delete(`/hotels/${id}`);
};

export const duplicateHotel = async (id: string): Promise<HotelDetail> => {
  const { data } = await apiClient.post<unknown>(`/hotels/${id}/duplicate`);
  return HotelDetailSchema.parse(data);
};

export const bulkDeleteHotels = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/hotels/bulk-delete', { ids });
  return data;
};

// ----- Rooms sub-resource -----

const HotelRoomListSchema = z.array(HotelRoomOutSchema);

export const listHotelRooms = async (hotelId: string): Promise<HotelRoomOut[]> => {
  const { data } = await apiClient.get<unknown>(`/hotels/${hotelId}/rooms`);
  return HotelRoomListSchema.parse(data);
};

export const createHotelRoom = async (
  hotelId: string,
  payload: CreateHotelRoomRequest,
): Promise<HotelRoomOut> => {
  const { data } = await apiClient.post<unknown>(`/hotels/${hotelId}/rooms`, payload);
  return HotelRoomOutSchema.parse(data);
};

export const updateHotelRoom = async (
  hotelId: string,
  roomId: string,
  payload: UpdateHotelRoomRequest,
): Promise<HotelRoomOut> => {
  const { data } = await apiClient.patch<unknown>(`/hotels/${hotelId}/rooms/${roomId}`, payload);
  return HotelRoomOutSchema.parse(data);
};

export const deleteHotelRoom = async (hotelId: string, roomId: string): Promise<void> => {
  await apiClient.delete(`/hotels/${hotelId}/rooms/${roomId}`);
};

// ----- HotelInventory sub-resource (B4.12) -----

export const listHotelInventory = async (
  hotelId: string,
  query: HotelInventoryListQuery,
): Promise<HotelInventoryListResponse> => {
  const { data } = await apiClient.get<unknown>(`/hotels/${hotelId}/inventory`, { params: query });
  return HotelInventoryListResponseSchema.parse(data);
};

export const createHotelInventory = async (
  hotelId: string,
  payload: CreateHotelInventoryRequest,
): Promise<HotelInventoryListItem> => {
  const { data } = await apiClient.post<unknown>(`/hotels/${hotelId}/inventory`, payload);
  return HotelInventoryListItemSchema.parse(data);
};

export const updateHotelInventory = async (
  hotelId: string,
  inventoryId: string,
  payload: UpdateHotelInventoryRequest,
): Promise<HotelInventoryListItem> => {
  const { data } = await apiClient.patch<unknown>(
    `/hotels/${hotelId}/inventory/${inventoryId}`,
    payload,
  );
  return HotelInventoryListItemSchema.parse(data);
};

export const deleteHotelInventory = async (hotelId: string, inventoryId: string): Promise<void> => {
  await apiClient.delete(`/hotels/${hotelId}/inventory/${inventoryId}`);
};
